/**
 * SettingsImport — Workspace import screen (Settings › Import).
 * Import guests, events, or resources from CSV or connected CRMs.
 * Shows recent import history with stat tiles.
 */
import { useState, useEffect, useRef, type CSSProperties } from "react";
import { alpha, Avatar, Btn, Card, Icon, IconTile, Pill, T, Table, tx, type TableColumn } from "@v0y4ge/jrni-ui";
import { StatTile } from "../../shell/composites";
import { PageHeader } from "../../shell/chrome";
import { can } from "../../shell/access";
import { toast } from "../../shell/toast";
import { useNav } from "../../shell/router";
import SettingsShell from "./SettingsShell";
import { listSettingsImports } from "@v0y4ge/api-client";
import { SETTINGS_OPTS } from "../../shell/request-opts";
import { useT, t as i18nT } from "../../lib/i18n";


const SOURCE_OPTIONS = [ // ui-config
  { id:"csv",        label:"CSV upload",       icon:"csv",  desc:"Upload a spreadsheet with guests, events, or resources." },
  { id:"salesforce", label:"Salesforce",        icon:"sf",   desc:"Pull contacts and opportunities from your Salesforce org." },
  { id:"hubspot",    label:"HubSpot",           icon:"h",    desc:"Import contacts and company data from HubSpot CRM." },
  { id:"excel",      label:"Excel / XLSX",      icon:"xl",   desc:"Upload an Excel file — we auto-detect columns." },
];

function SourceCard({ s, onSelect }: { s:typeof SOURCE_OPTIONS[0]; onSelect:()=>void }) {
  // ui-raw: bespoke JS-driven hover-highlight picker card (live border-color +
  // box-shadow glow on mouseenter/leave) — no jrni-ui component reproduces this
  // interaction (ProviderCard has no hover state; SelectableTile/ChoiceTile force
  // a radio/check indicator that isn't in the original design).
  const iconBg: Record<string,string> = { csv:"#1F8A5B", sf:"#00A1E0", h:"#FF7A59", xl:"#217346" }; // ui-raw: per-provider brand colors (HubSpot/Excel/CSV hexes have no jrni token or allow-list entry; Salesforce #00A1E0 is allow-listed)
  return (
    <button onClick={onSelect} style={{ textAlign:"left" as const, border:`1px solid ${T.border}`, borderRadius: T.radius.md, padding: `${T.space[4]} ${T.space[4]}`, display:"flex", gap: T.space[3], alignItems:"flex-start", cursor:"pointer", background:T.white, fontFamily:T.font, transition:"border-color 120ms, box-shadow 120ms" }}
      onMouseEnter={(e)=>{ e.currentTarget.style.borderColor=T.p1; e.currentTarget.style.boxShadow=`0 0 0 3px ${alpha(T.p1,"15")}`; }}
      onMouseLeave={(e)=>{ e.currentTarget.style.borderColor=T.border; e.currentTarget.style.boxShadow="none"; }}>
      <IconTile initials={s.icon} variant="solid" color={iconBg[s.icon]??T.p1} size="md" style={{ width:36, height:36, borderRadius: T.radius.md, fontWeight:800, fontSize: T.text.bodySm.fontSize }}/>
      <div>
        <div style={{ fontSize: T.text.body.fontSize, fontWeight:700, color:T.n1 }}>{s.label}</div>
        <div style={{ fontSize: T.text.label.fontSize, color:T.soft, marginTop: T.space[1] }}>{s.desc}</div>
      </div>
    </button>
  );
}

export default function SettingsImport() {
  const t = useT();
  const nav = useNav();
  const canWrite = can("settings.write");
  const [imports, setImports] = useState<any[] | null>(null);
  useEffect(() => {
    listSettingsImports({}, SETTINGS_OPTS).then((r: any) => {
      setImports(r.imports ?? r.data ?? []);
    }).catch(() => setImports([]));
  }, []);
  const [dragging, setDragging] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const colHeaderStyle: CSSProperties = { fontWeight:600, fontSize: T.text.label.fontSize, textTransform:"uppercase" as const, color:T.soft };

  const handleDrop = (file: File) => {
    toast({ tone:"success", title:t("settings.import.importStarted", "Import started"), subtitle:i18nT("settings.import.fileNameMappingColumns.icu", "{value0} · mapping columns…", { value0: file.name }) });
  };

  const totalAdded = (imports ?? []).reduce((s,r)=>s+r.added,0);
  const totalErrors = (imports ?? []).reduce((s,r)=>s+r.errors,0);

  const importColumns: TableColumn<any>[] = [
    {
      id: "file",
      header: <span style={colHeaderStyle}>{t("settings.import.file", "File")}</span>,
      cell: (r) => (
        <div style={{ display:"flex", gap: T.space[2], alignItems:"center" }}>
          <Icon.csv size={14} style={{ color:T.soft }}/>
          <span style={{ color:T.n1, fontWeight:500, fontFamily:T.fontMono, fontSize: T.text.label.fontSize }}>{r.file}</span>
        </div>
      ),
    },
    {
      id: "imported",
      header: <span style={colHeaderStyle}>{t("settings.import.imported", "Imported")}</span>,
      cell: (r) => <span style={{ color:T.soft, fontSize: T.text.label.fontSize }}>{r.at}</span>,
    },
    {
      id: "by",
      header: <span style={colHeaderStyle}>{t("settings.import.by", "By")}</span>,
      cell: (r) => (
        <span style={{ display:"inline-flex", alignItems:"center", gap: T.space[2], fontSize: T.text.bodySm.fontSize }}>
          {r.by==="Auto sync"
            ? <IconTile icon={t("settings.import.s", "S")} variant="solid" color="#00A1E0" size="sm" style={{ width:18, height:18, borderRadius: T.radius.sm, fontSize: T.text.label.fontSize, fontWeight:800 }}/>
            : <Avatar size={20} initials={r.by.split(" ").map((w: string)=>w[0]).join("")} color={T.p1}/>}
          {r.by}
        </span>
      ),
    },
    {
      id: "rows",
      header: <span style={colHeaderStyle}>{t("settings.import.rows", "Rows")}</span>,
      cell: (r) => <span style={{ color:T.n1, fontVariantNumeric:"tabular-nums" }}>{r.rows}</span>,
    },
    {
      id: "added",
      header: <span style={colHeaderStyle}>{t("settings.import.added", "Added")}</span>,
      cell: (r) => <span style={{ color:T.green1, fontVariantNumeric:"tabular-nums", fontWeight:600 }}>+{r.added}</span>,
    },
    {
      id: "updated",
      header: <span style={colHeaderStyle}>{t("settings.import.updated", "Updated")}</span>,
      cell: (r) => <span style={{ color:T.n1, fontVariantNumeric:"tabular-nums" }}>{r.updated}</span>,
    },
    {
      id: "errors",
      header: <span style={colHeaderStyle}>{t("settings.import.errors", "Errors")}</span>,
      cell: (r) => <span style={{ color:r.errors>0?T.red1:T.soft, fontVariantNumeric:"tabular-nums", fontWeight:r.errors>0?600:400 }}>{r.errors}</span>,
    },
    {
      id: "status",
      header: <span style={colHeaderStyle}>{t("settings.import.status", "Status")}</span>,
      cell: (r) =>
        r.status==="complete" ? <Pill tone="success" dot>{t("settings.import.complete", "Complete")}</Pill> :
        r.status==="partial"  ? <Pill tone="warn">{t("settings.import.partial", "Partial")}</Pill> :
                                 <Pill tone="danger">{t("common.status.failed", "Failed")}</Pill>,
    },
    {
      id: "actions",
      header: null,
      minWidth: 36,
      cell: (r) => (
        // ui-raw: plain transparent icon trigger — jrni-ui's IconButton always
        // renders a bordered/shadowed chrome (no style override), which doesn't
        // match this row-level ghost affordance.
        <button onClick={() => toast({ tone:"info", title:r.file, subtitle:i18nT("settings.import.rRowsRowsRAddedAddedRErrorsErrors.icu", "{value0} rows · {value1} added · {value2} errors", { value0: r.rows, value1: r.added, value2: r.errors }) })} style={{ border:"none", background:"transparent", color:T.soft, cursor:"pointer", padding: T.space[1], borderRadius: T.radius.sm, display:"inline-flex" }}>
          <Icon.info size={14}/>
        </button>
      ),
    },
  ];

  return (
    <SettingsShell activeTab="import">
      <PageHeader
        title={t("common.actions.import", "Import")}
        subtitle={t("settings.import.bringInGuestsEventsOrResourcesFromCsvFilesOrConnectedCrms", "Bring in guests, events, or resources from CSV files or connected CRMs")}
        actions={<Btn variant="secondary" icon={<Icon.download size={14}/>} onClick={() => toast({ tone:"info", title:t("settings.import.templateDownloaded", "Template downloaded"), subtitle:t("settings.import.guestsImportTemplateCsv", "guests-import-template.csv") })}>{t("settings.import.downloadTemplate", "Download template")}</Btn>}
      />
      <div style={{ flex:1, overflow:"auto", padding: `${T.space[4]} ${T.space[6]} ${T.space[10]}`, display:"flex", flexDirection:"column", gap: T.space[5] }}>
        {/* Stats */}
        {/* §3f (2026-07-19): "Imports · 30d" + "Auto syncs" removed — fabricated
            counts must not sit beside real totals (getImportStats declared).
            Labels drop the 30d claim: these totals derive from the loaded list. */}
        <div style={{ display:"grid", gridTemplateColumns:"repeat(2, 1fr)", gap: T.space[3] }}>
          <StatTile label={t("settings.import.guestsAdded", "Guests added")}  value={totalAdded.toLocaleString()}  icon={<Icon.users size={14}/>} accent={T.green1}/>
          <StatTile label={t("settings.import.importErrors", "Import errors")} value={totalErrors.toLocaleString()} icon={<Icon.warn size={14}/>}  accent={T.red1}/>
        </div>

        {/* Import sources */}
        {canWrite ? (
          <Card style={{ padding: T.space[6] }}>
            <h3 style={{ ...tx.h3, fontWeight:700, marginBottom: T.space[4] }}>{t("settings.import.startANewImport", "Start a new import")}</h3>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(2, 1fr)", gap: T.space[3], marginBottom: T.space[5] }}>
              {SOURCE_OPTIONS.map((s) => (
                <SourceCard key={s.id} s={s} onSelect={() => {
                  if (s.id==="csv"||s.id==="excel") fileRef.current?.click();
                  else toast({ tone:"info", title:i18nT("settings.import.sLabelImport.icu", "{value0} import", { value0: s.label }), subtitle:t("settings.import.openingConnectionSettings", "Opening connection settings…") });
                }}/>
              ))}
            </div>
            {/* Drag-drop zone */}
            <div
              onDragOver={(e)=>{ e.preventDefault(); setDragging(true); }}
              onDragLeave={()=>setDragging(false)}
              onDrop={(e)=>{ e.preventDefault(); setDragging(false); if(e.dataTransfer.files[0]) handleDrop(e.dataTransfer.files[0]); }}
              onClick={()=>fileRef.current?.click()}
              style={{ border:`2px dashed ${dragging?T.p1:T.n4}`, borderRadius: T.radius.lg, padding: `${T.space[8]} ${T.space[6]}`, textAlign:"center" as const, cursor:"pointer", background:dragging?T.p4:T.n6, transition:"all 150ms", display:"flex", flexDirection:"column", alignItems:"center", gap: T.space[3] }}>
              <span style={{ width:48, height:48, borderRadius: T.radius.lg, background:T.p4, display:"inline-flex", alignItems:"center", justifyContent:"center" }}><Icon.upload size={22} style={{ color:T.p1 }}/></span>
              <div style={{ fontSize: T.text.body.fontSize, fontWeight:600, color:T.n1 }}>{t("settings.import.dragDropYourFileHere", "Drag & drop your file here")}</div>
              <div style={{ fontSize: T.text.bodySm.fontSize, color:T.soft }}>{t("settings.import.csvXlsxOrXlsMax10mbUpTo50000Rows", "CSV, XLSX, or XLS · max 10MB · up to 50,000 rows")}</div>
              <Btn variant="secondary" size="sm">{t("settings.import.browseFiles", "Browse files")}</Btn>
            </div>
            {/* ui-raw: hidden native file-picker trigger (type="file", display:none, opened via ref) — jrni-ui's Input is a labeled text-entry field, not a file input, so there is no drop-in replacement */}
            <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" style={{ display:"none" }} onChange={(e)=>{ if(e.target.files?.[0]) handleDrop(e.target.files[0]); }}/>
          </Card>
        ) : null}

        {/* Import history */}
        <Card style={{ padding:0 }}>
          <div style={{ padding: `${T.space[4]} ${T.space[4]}`, borderBottom:`1px solid ${T.border}`, fontSize: T.text.h3.fontSize, fontWeight:700, color:T.n1 }}>{t("settings.import.importHistory", "Import history")}</div>
          <Table
            columns={importColumns}
            rows={imports ?? []}
            getRowId={(r) => r.id}
            emptyState=""
            style={{ border:"none", borderRadius: T.radius.none, fontSize: T.text.bodySm.fontSize }}
          />
        </Card>
      </div>
    </SettingsShell>
  );
}
